import React, { useEffect, useState } from 'react';
import { Content } from '@backstage/core-components';
import {
  getLandingCards,
  LOGIN_ACTION,
  CONTACT_US_ROUTE,
  portalCapabilities,
} from './LandingPage.constants';
import { LandingCard, LandingPageProps } from './LandingPage.types';
import {
  CardsGrid,
  BannerContainer,
  BannerContent,
  BannerOverlay,
  BannerSubtitle,
  BannerTitle,
  LandingMain,
  LandingPageWrapper,
  AuthButton,
  OptionBody,
  OptionCard,
  OptionDescription,
  OptionMedia,
  OptionTitle,
  CapabilitiesWrapper,
  CapabilitiesGrid,
  FunctionIcon,
  CardsColumn,
  PortalCapabilitiesWrapper,
  CapabilityTitle,
  CapabilitySubtitle,
  RowSection,
  CapabilityChip,
} from './LandingPage.styles';
import { useNavigate } from 'react-router-dom';
import { configApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';
import { useIdentity } from '@internal/shared';
import { useOpenLogin } from '../SignIn/useOpenLogin';

export function LandingPage({
  onLoginClick,
  onContactClick,
}: LandingPageProps) {
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const errorApi = useApi(errorApiRef);
  const { idToken, identityApi } = useIdentity();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const useDocsV2 = Boolean(
    configApi.getOptionalBoolean('featureFlags.docsV2'),
  );
  const landingCards = getLandingCards(useDocsV2);

  useEffect(() => {
    setIsUserLoggedIn(Boolean(idToken));
  }, [idToken]);

  function getCardTarget(card: LandingCard, loggedIn: boolean) {
    if (card.to === CONTACT_US_ROUTE) return CONTACT_US_ROUTE;
    if (card.to === LOGIN_ACTION)
      return loggedIn ? card.redirectTo : LOGIN_ACTION;
    return card.to;
  }

  const openLogin = useOpenLogin();
  const handleLoginClick = onLoginClick ?? openLogin;

  const handleLogoutClick = () => {
    identityApi?.signOut().catch((error: Error) => errorApi.post(error));
  };

  const handleCardClick = (card: LandingCard) => {
    if (card.to === CONTACT_US_ROUTE) {
      if (onContactClick) {
        onContactClick();
        return;
      }
      navigate(CONTACT_US_ROUTE);
      return;
    }

    const target = getCardTarget(card, isUserLoggedIn);
    if (target === LOGIN_ACTION) {
      handleLoginClick();
      return;
    }
    if (target) navigate(target);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent, card: LandingCard) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick(card);
    }
  };

  return (
    <Content>
      <LandingPageWrapper>
        <LandingMain>
          <BannerContainer>
            <BannerOverlay />
            {!isUserLoggedIn ? (
              <AuthButton variant="contained" onClick={handleLoginClick}>
                Login / Register
              </AuthButton>
            ) : (
              <AuthButton variant="contained" onClick={handleLogoutClick}>
                Log out
              </AuthButton>
            )}
            <BannerContent>
              <BannerTitle variant="h2">
                Welcome to Developer Experience Portal
              </BannerTitle>
              <BannerSubtitle variant="subtitle1">
                A secure and efficient way to integrate Digital Realty Data
                Center services and products directly into your own systems.
                Build and adapt digital solutions as your business evolves.
              </BannerSubtitle>
            </BannerContent>
          </BannerContainer>

          <RowSection>
            <CardsColumn>
              <CardsGrid gridLength={isUserLoggedIn ? 4 : 3}>
                {landingCards
                  .filter(
                    card => card.showForUnregisteredUsers || isUserLoggedIn,
                  )
                  .map(card => (
                    <OptionCard
                      key={card.title}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleCardClick(card)}
                      onKeyDown={event => handleCardKeyDown(event, card)}
                    >
                      <OptionMedia
                        style={{ backgroundImage: `url(${card.image})` }}
                      />
                      <OptionBody>
                        <OptionTitle variant="h5">{card.title}</OptionTitle>
                        <OptionDescription variant="body2">
                          {card.description}
                        </OptionDescription>
                      </OptionBody>
                    </OptionCard>
                  ))}
              </CardsGrid>
            </CardsColumn>
            {!isUserLoggedIn && (
              <PortalCapabilitiesWrapper>
                <CapabilityTitle variant="h3">
                  Login or Register to begin your digital integration journey
                </CapabilityTitle>
                <CapabilitySubtitle variant="h6">
                  Portal Functionality
                </CapabilitySubtitle>
                <CapabilitiesWrapper>
                  <CapabilitiesGrid>
                    {portalCapabilities.map(item => (
                      <CapabilityChip
                        key={item.label}
                        icon={<FunctionIcon src={item.icon} alt={item.label} />}
                        label={item.label}
                        role="presentation"
                        color="primary"
                      />
                    ))}
                  </CapabilitiesGrid>
                </CapabilitiesWrapper>
              </PortalCapabilitiesWrapper>
            )}
          </RowSection>
        </LandingMain>
      </LandingPageWrapper>
    </Content>
  );
}
